import moment from 'moment'

import { COPY } from '../constants/config'

import { EnvironmentConfig as Config } from './EnvironmentConfigContext'
import {
	A_SECOND,
	A_MINUTE,
	AN_HOUR,
	A_DAY,
	A_WEEK,
	A_YEAR,
	timestampToMilliseconds,
} from './time'

function zeroPad(num: number) {
	if (num > 9) {
		return `${num}`
	}
	return `0${num}`
}

export const toTimerFormat = (milliseconds: number) => {
	let remainder: number

	const hours = Math.floor(milliseconds / AN_HOUR)
	const h = hours
	remainder = milliseconds % AN_HOUR

	const minutes = Math.floor(remainder / A_MINUTE)
	const mm = zeroPad(minutes)
	remainder = milliseconds % A_MINUTE

	const seconds = Math.floor(remainder / A_SECOND)
	const ss = zeroPad(seconds)

	return h ? `${h}:${mm}:${ss}` : `${minutes}:${ss}`
}

export const archiveTimeSinceMillis = (
	dateInMillis: number,
	referenceDateInMillis: number
) => {
	const difference = referenceDateInMillis - dateInMillis
	const date = new Date(dateInMillis)
	const referenceDate = new Date(referenceDateInMillis)

	if (difference < A_DAY) {
		if (date.getDay() !== referenceDate.getDay()) {
			return COPY.TIME.DAY_NAME_ABBREVIATIONS[date.getDay()]
		}
		if (date.getDay() - referenceDate.getDay()) {
			return 'Yesterday'
		}
		return `${zeroPad(date.getHours())}:${zeroPad(date.getMinutes())}`
	}
	if (difference < A_WEEK) {
		return COPY.TIME.DAY_NAME_ABBREVIATIONS[date.getDay()]
	}
	return date.toLocaleDateString()
}

export function timeSinceMillis(
	dateInMillis: number,
	referenceDateInMillis: number = Date.now(),
	{ momentsAgoUntilMillis = 10 * A_SECOND } = {}
) {
	const difference = referenceDateInMillis - dateInMillis

	if (difference < momentsAgoUntilMillis) {
		return 'moments ago'
	}
	if (difference < A_MINUTE) {
		const interval = Math.floor(difference / A_SECOND)
		return `${interval} seconds ago`
	}
	if (difference < AN_HOUR) {
		const interval = Math.floor(difference / A_MINUTE)
		return `${interval} ${interval === 1 ? 'minute' : 'minutes'} ago`
	}
	if (difference < A_DAY) {
		const interval = Math.floor(difference / AN_HOUR)
		return `${interval} ${interval === 1 ? 'hour' : 'hours'} ago`
	}
	if (difference < A_WEEK) {
		const interval = Math.floor(difference / A_DAY)
		return `${interval} ${interval === 1 ? 'day' : 'days'} ago`
	}
	if (difference < A_YEAR) {
		const interval = Math.floor(difference / A_WEEK)
		return `${interval} ${interval === 1 ? 'week' : 'weeks'} ago`
	}
	const interval = Math.floor(difference / A_YEAR)
	return `${interval} ${interval === 1 ? 'year' : 'years'} ago`
}

export const shortFormatNumber = (number: number): string =>
	number > 99 ? '99+' : number.toString()

export const historicalDayFormat = (
	dateInMillis: number,
	nowInMillis: number
): string => {
	const date = new Date(dateInMillis)
	const nowDate = new Date(nowInMillis)
	const timeDifference = nowInMillis - dateInMillis

	// If within 24h..
	if (timeDifference <= A_DAY) {
		// ..it's either Today, if dates are the same
		if (date.getDate() === nowDate.getDate()) {
			return 'Today'
		}
		// ..or it's Yesterday
		return 'Yesterday'
	}

	// If within a week but not on the same day of the week as now..
	if (timeDifference <= A_WEEK && date.getDate() !== nowDate.getDate()) {
		// ..it's either Yesterday if it's the previous day of the week
		if ((nowDate.getDay() - date.getDay() + 7) % 7 === 1) {
			return 'Yesterday'
		}

		// ..or it's some other day of the week
		return COPY.TIME.DAY_NAMES[date.getDay()]
	}

	// By default, print "Day_of_Week, Day Month"
	return `${COPY.TIME.DAY_NAMES[date.getDay()]}, ${date.getDate()} ${
		COPY.TIME.MONTH_NAMES[date.getMonth()]
	}`
}

export const colorOrDefault = (color: string | undefined, fallback: string) =>
	colorPrefixer(color ?? fallback)

export const colorPrefixer = (color: string) => {
	switch (true) {
		case color.startsWith('rgb'):
		case color.startsWith('#'):
			return color
		default:
			return `#${color}`
	}
}

/*
	Copyright (c) 2018-2020 Uber Technologies, Inc.
	MIT License | https://github.com/uber/baseweb
*/
export function hexToRgb(hex: string = '', alpha: string = '1') {
	const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
	hex = hex.replace(shorthandRegex, function (m, r, g, b) {
		return r + r + g + g + b + b
	})
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
	return result
		? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
				result[3],
				16
		  )}, ${alpha})`
		: null
}

export enum MessageType {
	Text = 'text',
	Image = 'image',
	Announcement = 'announcement',
}

export const placeholderForMessageType = (
	messageType: MessageType,
	canMention: boolean
) => {
	switch (messageType) {
		case MessageType.Text:
			if (canMention) {
				return 'Type a message, @name...'
			}
			return 'Type a message...'
		case MessageType.Image:
			return 'Add caption...'
		case MessageType.Announcement:
			return 'Type an announcement...'
		default:
			return undefined
	}
}

// new moment based function
// TODO: check if Luxon is better with locales: https://moment.github.io/luxon/docs/manual/why.html
moment.locale(Config.locale())
moment.updateLocale(Config.locale(), {
	calendar: {
		lastDay: '[Yesterday at] LT',
		sameDay: 'LT',
		nextDay: '[Tomorrow at] LT',
		lastWeek: '[last] dddd',
		nextWeek: 'dddd [at] LT',
		sameElse: 'L',
	},
})

export function feedItemTime(date?: string | null | moment.Moment | Date) {
	if (!date) {
		return ''
	}

	if (typeof date === 'string' || date instanceof Date) {
		date = moment(date)
	}

	return date.calendar(Config.getReferenceDate())
}

interface Timestamp {
	seconds: number
	nanoseconds: number
}

export function formatTimestamp(input?: Timestamp): string {
	if (!input) {
		return ''
	}

	const millis = timestampToMilliseconds(input.seconds, input.nanoseconds)
	return feedItemTime(new Date(millis))
}
